import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Topbar from '../Components/Topbar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  createOrderFlowUser,
  createOrderForUser,
  createUserAddress,
  fetchServicesForOrderFlow,
  getErrorMessage,
  getUserAddresses,
  getUsersByMobile,
  uploadAdminImageFile,
} from '../Config/adminOrderFlowService';
import {
  fetchPlaceDetailsById,
  useGooglePlacesPredictions,
} from '../hooks/useGooglePlacesAutocomplete';
import { parseGooglePlaceToOurAddress } from '../Utils/googlePlaceToAddress';
import { formatCentsAsCurrency } from '../Utils/money';

const STEPS = [
  { id: 1, title: 'Customer', short: '1' },
  { id: 2, title: 'Address', short: '2' },
  { id: 3, title: 'Order', short: '3' },
];

function parseList(str) {
  if (!str || !String(str).trim()) return [];
  return String(str)
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function inputClass(extra = '') {
  return `w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 ${extra}`;
}

function labelClass() {
  return 'block text-sm font-medium text-gray-700 mb-1';
}

function Panel({ title, subtitle, children }) {
  return (
    <section className="rounded-2xl bg-white shadow-sm ring-1 ring-gray-200 overflow-hidden">
      <div className="border-b border-gray-100 bg-gray-50/80 px-4 py-3 sm:px-5">
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        {subtitle ? <p className="mt-0.5 text-sm text-gray-500">{subtitle}</p> : null}
      </div>
      <div className="p-4 sm:p-5">{children}</div>
    </section>
  );
}

function StepHeader({ currentStep, onStepClick }) {
  return (
    <nav aria-label="Progress" className="mb-6">
      <ol className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
        {STEPS.map((s, i) => {
          const active = currentStep === s.id;
          const done = currentStep > s.id;
          const clickable = s.id < currentStep;
          return (
            <li key={s.id} className="flex flex-1 min-w-0">
              <button
                type="button"
                disabled={!clickable}
                onClick={() => clickable && onStepClick(s.id)}
                className={`flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition sm:py-3 ${
                  active
                    ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-500/20'
                    : done
                      ? 'border-green-200 bg-green-50/50'
                      : 'border-gray-200 bg-white'
                } ${clickable ? 'cursor-pointer hover:border-indigo-300' : ''} ${
                  !clickable && !active ? 'opacity-80' : ''
                }`}
              >
                <span
                  className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                    active
                      ? 'bg-indigo-600 text-white'
                      : done
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {done ? '✓' : s.short}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-gray-900">{s.title}</span>
                  <span className="block text-xs text-gray-500">
                    Step {i + 1} of {STEPS.length}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function AdminOrderCreate() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [mobileInput, setMobileInput] = useState('');
  /** Mobile number we last successfully searched; used to show “new customer” only after a search with zero hits. */
  const [searchedMobile, setSearchedMobile] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    gender: '',
    age: '',
    business_name: '',
  });

  const [addresses, setAddresses] = useState([]);
  const [addrLoading, setAddrLoading] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [newAddr, setNewAddr] = useState({
    line1: '',
    line2: '',
    area: '',
    city: '',
    pincode: '',
    label: '',
    latitude: '',
    longitude: '',
    skip_serviceability: false,
  });
  const [addrSubmitting, setAddrSubmitting] = useState(false);
  const mapsApiKey = (process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '').trim();
  const [addressSearchText, setAddressSearchText] = useState('');
  const [addressPickHighlight, setAddressPickHighlight] = useState(0);
  const [addressPickLoading, setAddressPickLoading] = useState(false);
  /** After a pick, input shows full formatted address — that string re-triggers Places; pause until user edits. */
  const [addressPredictionsPaused, setAddressPredictionsPaused] = useState(false);
  const [showManualAddressFields, setShowManualAddressFields] = useState(() => !mapsApiKey);

  const {
    ready: mapsReady,
    loadError: mapsLoadError,
    predictions: addressPredictions,
    predictionsError: addressPredictionsError,
    loading: addressPredictLoading,
    clearPredictions: clearAddressPredictions,
  } = useGooglePlacesPredictions({
    apiKey: mapsApiKey,
    searchText: addressSearchText,
    enabled: currentStep === 2 && Boolean(mapsApiKey) && !addressPredictionsPaused,
    minChars: 3,
  });

  const [services, setServices] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [serviceSearch, setServiceSearch] = useState('');
  const [serviceMenuOpen, setServiceMenuOpen] = useState(false);
  const [serviceHighlightIdx, setServiceHighlightIdx] = useState(0);
  /** Preferred start date-times (customer/admin options); first is primary `expected_started_at`. */
  const [preferredSlots, setPreferredSlots] = useState(() => [
    new Date(Date.now() + 3600000),
  ]);
  const [orderForm, setOrderForm] = useState({
    service_id: '',
    preferences: '',
    is_insured: false,
    user_remark: '',
    build_year: '',
    model: '',
    company: '',
    colour: '',
    vin_no: '',
    quantity: '1',
  });
  const [orderUserImages, setOrderUserImages] = useState([]);
  const [orderUserImagesUploading, setOrderUserImagesUploading] = useState(false);
  const orderImagesInputRef = useRef(null);
  const [createSubmitting, setCreateSubmitting] = useState(false);

  const showRegisterNewCustomer =
    searchedMobile != null &&
    searchedMobile === mobileInput.trim() &&
    candidates.length === 0;

  const selectedService = useMemo(() => {
    if (!orderForm.service_id) return null;
    return services.find((s) => s.service_id === orderForm.service_id) || null;
  }, [orderForm.service_id, services]);

  const filteredServices = useMemo(() => {
    const q = serviceSearch.trim().toLowerCase();
    if (!q) return services.slice(0, 80);
    return services.filter(
      (s) =>
        s.title?.toLowerCase().includes(q) ||
        String(s.service_id || '').toLowerCase().includes(q)
    );
  }, [services, serviceSearch]);

  const servicePickLabel = useCallback((s) => {
    if (!s) return '';
    return `${s.title} — ${formatCentsAsCurrency(s.amount, s.currency || 'CAD')}${
      s.service_type ? ` (${s.service_type})` : ''
    }`;
  }, []);

  const pickService = useCallback(
    (s) => {
      if (!s?.service_id) return;
      setOrderForm((f) => ({ ...f, service_id: s.service_id }));
      setServiceSearch(servicePickLabel(s));
      setServiceMenuOpen(false);
    },
    [servicePickLabel]
  );

  useEffect(() => {
    setServiceHighlightIdx(0);
  }, [filteredServices]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setCatalogLoading(true);
        const svc = await fetchServicesForOrderFlow();
        if (!cancelled) {
          setServices(svc);
        }
      } catch (e) {
        if (!cancelled) toast.error(e.message || 'Failed to load catalog');
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadAddresses = useCallback(async (userId) => {
    if (!userId) return;
    setAddrLoading(true);
    try {
      const { ok, body } = await getUserAddresses(userId);
      if (ok && body.success) {
        setAddresses(Array.isArray(body.data) ? body.data : []);
      } else {
        toast.error(getErrorMessage(body, 'Failed to load addresses'));
        setAddresses([]);
      }
    } catch {
      toast.error('Failed to load addresses');
      setAddresses([]);
    } finally {
      setAddrLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedUser?.user_id) {
      loadAddresses(selectedUser.user_id);
      setSelectedAddressId(null);
    } else {
      setAddresses([]);
      setSelectedAddressId(null);
    }
  }, [selectedUser, loadAddresses]);

  useEffect(() => {
    if (currentStep !== 2) setAddressPredictionsPaused(false);
  }, [currentStep]);

  const applyPlaceToNewAddr = useCallback((place) => {
    const parsed = parseGooglePlaceToOurAddress(place);
    if (!Number.isFinite(parsed.latitude) || !Number.isFinite(parsed.longitude)) {
      toast.error('Could not read coordinates for that place — try another result or enter manually.');
      return;
    }
    setNewAddr((a) => ({
      ...a,
      line1: parsed.line1,
      line2: parsed.line2,
      area: parsed.area,
      city: (parsed.city || '').toLowerCase().trim(),
      pincode: parsed.pincode,
      latitude: String(parsed.latitude),
      longitude: String(parsed.longitude),
    }));
    const display = parsed.formattedAddress || '';
    setAddressSearchText(display);
    setAddressPredictionsPaused(true);
    toast.success('Address filled from map');
  }, []);

  const selectAddressPrediction = useCallback(
    async (prediction) => {
      if (!prediction?.place_id || addressPickLoading) return;
      setAddressPickLoading(true);
      try {
        const place = await fetchPlaceDetailsById(prediction.place_id);
        applyPlaceToNewAddr(place);
        clearAddressPredictions();
      } catch (e) {
        toast.error(e?.message || 'Could not load this address');
      } finally {
        setAddressPickLoading(false);
      }
    },
    [addressPickLoading, applyPlaceToNewAddr, clearAddressPredictions]
  );

  useEffect(() => {
    setAddressPickHighlight(0);
  }, [addressPredictions]);

  const handleLookup = async () => {
    if (!mobileInput.trim()) {
      toast.error('Enter a mobile number');
      return;
    }
    setLookupLoading(true);
    setCandidates([]);
    const m = mobileInput.trim();
    try {
      const { ok, body } = await getUsersByMobile(m);
      if (ok && body.success) {
        const list = Array.isArray(body.data) ? body.data : [];
        setCandidates(list);
        setSearchedMobile(m);
        if (list.length === 0) {
          toast.info('No account for this number — you can register a new customer below.');
        } else {
          toast.success(`Found ${list.length} user(s)`);
        }
      } else {
        setSearchedMobile(null);
        toast.error(getErrorMessage(body, 'Lookup failed'));
      }
    } catch {
      setSearchedMobile(null);
      toast.error('Lookup failed');
    } finally {
      setLookupLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!mobileInput.trim()) {
      toast.error('Mobile is required');
      return;
    }
    setLookupLoading(true);
    try {
      const payload = {
        mobile: mobileInput.trim(),
        name: newUser.name || undefined,
        email: newUser.email || undefined,
        gender: newUser.gender || undefined,
        business_name: newUser.business_name || undefined,
      };
      if (newUser.age !== '' && !Number.isNaN(Number(newUser.age))) {
        payload.age = Number(newUser.age);
      }
      const { ok, status, body } = await createOrderFlowUser(payload);
      if (ok && body.success && body.data) {
        setSelectedUser(body.data);
        setCandidates([body.data]);
        setCurrentStep(2);
        toast.success('User created');
      } else if (status === 409) {
        toast.error(getErrorMessage(body, 'Mobile already registered — use lookup'));
      } else {
        toast.error(getErrorMessage(body, 'Could not create user'));
      }
    } catch {
      toast.error('Could not create user');
    } finally {
      setLookupLoading(false);
    }
  };

  const handleCreateAddress = async () => {
    if (!selectedUser?.user_id) return;
    const lat = Number(newAddr.latitude);
    const lng = Number(newAddr.longitude);
    if (!newAddr.line1.trim() || !newAddr.city.trim() || !newAddr.pincode.trim()) {
      toast.error('Line 1, city, and postal code are required');
      return;
    }
    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      toast.error('Valid latitude and longitude are required — pick an address from search or enter coordinates');
      return;
    }
    setAddrSubmitting(true);
    try {
      const payload = {
        line1: newAddr.line1.trim(),
        city: newAddr.city.trim().toLowerCase(),
        pincode: newAddr.pincode.trim(),
        latitude: lat,
        longitude: lng,
        line2: newAddr.line2 || undefined,
        area: newAddr.area || undefined,
        label: newAddr.label || undefined,
      };
      const { ok, body } = await createUserAddress(selectedUser.user_id, payload, {
        skipServiceabilityCheck: newAddr.skip_serviceability,
      });
      if (ok && body.success && body.data) {
        toast.success('Address added');
        setAddressSearchText('');
        setAddressPredictionsPaused(false);
        clearAddressPredictions();
        setNewAddr((a) => ({
          ...a,
          line1: '',
          line2: '',
          area: '',
          city: '',
          pincode: '',
          latitude: '',
          longitude: '',
          label: '',
        }));
        await loadAddresses(selectedUser.user_id);
        const newId = body.data.address_id;
        setSelectedAddressId(newId != null ? newId : null);
        setCurrentStep(3);
      } else {
        toast.error(getErrorMessage(body, 'Could not add address'));
      }
    } catch {
      toast.error('Could not add address');
    } finally {
      setAddrSubmitting(false);
    }
  };

  const sortedUniqueSlotIsos = () => {
    const valid = preferredSlots.filter(
      (d) => d instanceof Date && !Number.isNaN(d.getTime())
    );
    const isos = [...new Set(valid.map((d) => d.toISOString()))].sort(
      (a, b) => new Date(a) - new Date(b)
    );
    return isos;
  };

  const buildOrderPayload = () => {
    const slotIsos = sortedUniqueSlotIsos();

    const payload = {
      user_id: selectedUser.user_id,
      service_id: orderForm.service_id,
      address_id: selectedAddressId,
      expected_started_at: slotIsos[0],
    };
    if (slotIsos.length > 1) {
      payload.time_options = slotIsos;
    }

    const prefs = parseList(orderForm.preferences);
    if (prefs.length) payload.preferences = prefs;
    if (orderForm.is_insured) payload.is_insured = true;
    const remark = orderForm.user_remark?.trim();
    if (remark) payload.user_remark = remark;
    if (orderUserImages.length) payload.user_images = [...orderUserImages];

    const st = selectedService?.service_type;
    if (st === 'QUANTITY') {
      const q = parseInt(String(orderForm.quantity ?? '1').trim(), 10);
      payload.quantity = Number.isNaN(q) ? 1 : Math.max(1, q);
    }
    if (st === 'AUTO') {
      const y = parseInt(String(orderForm.build_year).trim(), 10);
      if (!Number.isNaN(y)) payload.build_year = y;
      payload.model = orderForm.model?.trim() || undefined;
      payload.company = orderForm.company?.trim() || undefined;
      payload.colour = orderForm.colour?.trim() || undefined;
      payload.vin_no = orderForm.vin_no?.trim() || undefined;
    }
    return payload;
  };

  const handleCreateOrder = async () => {
    if (!selectedUser?.user_id) {
      toast.error('Select or create a customer first');
      return;
    }
    if (!selectedAddressId) {
      toast.error('Select or add a service address');
      return;
    }
    if (!orderForm.service_id) {
      toast.error('Select a service');
      return;
    }
    const st = selectedService?.service_type;
    if (st === 'AUTO') {
      const year = parseInt(String(orderForm.build_year).trim(), 10);
      if (Number.isNaN(year) || year < 1900 || year > 2100) {
        toast.error('Enter a valid build year (1900–2100)');
        return;
      }
    }

    const slotIsos = sortedUniqueSlotIsos();
    if (slotIsos.length === 0) {
      toast.error('Add at least one preferred start time');
      return;
    }

    setCreateSubmitting(true);
    try {
      const payload = buildOrderPayload();
      const { ok, body } = await createOrderForUser(payload);
      const data = body?.data;
      const ord = data?.order ?? data;
      const orderId = ord?.order_id;

      const failed = !ok || body?.success === false || orderId == null;
      if (failed) {
        toast.error(getErrorMessage(body, 'Could not create order'));
        return;
      }

      const statusUpper = String(ord?.status || '').toUpperCase();
      const payBlock = data?.payment ?? body?.payment;
      const paymentHint =
        data?.payment_message ||
        data?.payment_error ||
        body?.payment_message ||
        body?.payment_error ||
        (payBlock &&
          typeof payBlock === 'object' &&
          !Array.isArray(payBlock) &&
          (payBlock.message || payBlock.error));

      if (statusUpper === 'DRAFT') {
        toast.warning(
          'Order created as DRAFT (payment may be pending or skipped). Fix payment from order details if needed.'
        );
      } else if (statusUpper === 'PENDING') {
        toast.success('Order created and is pending.');
      } else {
        toast.success(`Order created (${statusUpper || ord?.status || 'ok'}).`);
      }
      if (paymentHint) {
        toast.info(String(paymentHint));
      }
      navigate(`/orders/${orderId}`);
    } catch {
      toast.error('Could not create order');
    } finally {
      setCreateSubmitting(false);
    }
  };

  const canGoStep2 = !!selectedUser?.user_id;
  const canGoStep3 = canGoStep2 && !!selectedAddressId;

  return (
    <div className="min-h-screen bg-gray-100">
      <Topbar />
      <ToastContainer position="top-right" />

      <main className="mx-auto max-w-4xl px-3 py-6 sm:px-4 sm:py-8 lg:max-w-5xl">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
              Create order for customer
            </h1>
            <p className="mt-1 text-sm text-gray-600 sm:text-base">
              After creation you will be taken to the order details page to assign contractors, run
              job steps, and complete payment.
            </p>
          </div>
          <Link
            to="/orders"
            className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            Back to orders
          </Link>
        </div>

        <StepHeader
          currentStep={currentStep}
          onStepClick={(stepId) => {
            if (stepId === 2 && !canGoStep2) {
              toast.error('Select or create a customer first');
              return;
            }
            if (stepId === 3 && !canGoStep3) {
              toast.error('Pick a delivery address first');
              return;
            }
            setCurrentStep(stepId);
          }}
        />

        <div className="space-y-6">
          {currentStep === 1 && (
            <Panel
              title="Find or create customer"
              subtitle="Search by mobile first. The new-customer form only appears when no account exists for that number."
            >
              <div className="space-y-4">
                <div className="flex flex-col gap-2 sm:flex-row">
                  <input
                    type="tel"
                    className={inputClass('sm:flex-1')}
                    placeholder="+1… or digits"
                    value={mobileInput}
                    onChange={(e) => {
                      const v = e.target.value;
                      setMobileInput(v);
                      if (searchedMobile != null && v.trim() !== searchedMobile) {
                        setCandidates([]);
                        setSearchedMobile(null);
                      }
                    }}
                    autoComplete="tel"
                  />
                  <button
                    type="button"
                    onClick={handleLookup}
                    disabled={lookupLoading}
                    className="rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
                  >
                    {lookupLoading ? 'Searching…' : 'Search'}
                  </button>
                </div>

                {candidates.length > 0 && (
                  <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white">
                    {candidates.map((u) => (
                      <li key={u.user_id} className="p-3 sm:p-4">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedUser(u);
                            setCurrentStep(2);
                            toast.success('Customer selected');
                          }}
                          className="flex w-full text-left"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-gray-900">{u.name || 'No name'}</div>
                            <div className="text-sm text-gray-500">{u.mobile}</div>
                            {u.email ? (
                              <div className="text-xs text-gray-400">{u.email}</div>
                            ) : null}
                          </div>
                          <span className="text-indigo-600 text-sm font-medium">Select →</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                {showRegisterNewCustomer ? (
                  <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50/80 p-4">
                    <h3 className="text-sm font-semibold text-gray-800">New customer</h3>
                    <p className="mt-1 text-xs text-gray-600 sm:text-sm">
                      No account for <strong>{mobileInput.trim()}</strong>. Add optional details, then register. If this
                      number is already taken, you will see an error.
                    </p>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      <div>
                        <label className={labelClass()}>Name</label>
                        <input
                          className={inputClass()}
                          value={newUser.name}
                          onChange={(e) => setNewUser((n) => ({ ...n, name: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className={labelClass()}>Email</label>
                        <input
                          type="email"
                          className={inputClass()}
                          value={newUser.email}
                          onChange={(e) => setNewUser((n) => ({ ...n, email: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className={labelClass()}>Gender</label>
                        <input
                          className={inputClass()}
                          value={newUser.gender}
                          onChange={(e) => setNewUser((n) => ({ ...n, gender: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className={labelClass()}>Age</label>
                        <input
                          type="number"
                          className={inputClass()}
                          value={newUser.age}
                          onChange={(e) => setNewUser((n) => ({ ...n, age: e.target.value }))}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className={labelClass()}>Business name</label>
                        <input
                          className={inputClass()}
                          value={newUser.business_name}
                          onChange={(e) =>
                            setNewUser((n) => ({ ...n, business_name: e.target.value }))
                          }
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleCreateUser}
                      disabled={lookupLoading || !mobileInput.trim()}
                      className="mt-4 rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-black disabled:opacity-50"
                    >
                      Register user
                    </button>
                  </div>
                ) : null}
              </div>
            </Panel>
          )}

          {currentStep === 2 && (
            <Panel
              title="Service address"
              subtitle={
                mapsApiKey
                  ? 'Search for an address or enter it manually below.'
                  : 'Enter the address in the form below (or add a Google Maps API key to .env to enable search).'
              }
            >
              {selectedUser ? (
                <p className="mb-4 text-sm text-gray-600">
                  Customer: <strong>{selectedUser.name || '—'}</strong> ({selectedUser.mobile})
                </p>
              ) : null}

              {addrLoading ? (
                <p className="text-gray-500">Loading addresses…</p>
              ) : addresses.length === 0 ? (
                <p className="mb-4 text-sm text-amber-800">No saved addresses yet. Add one below.</p>
              ) : (
                <ul className="mb-6 space-y-2">
                  {addresses.map((a) => (
                    <li key={a.address_id}>
                      <label className="flex cursor-pointer gap-3 rounded-xl border border-gray-200 bg-white p-3 has-[:checked]:border-indigo-500 has-[:checked]:ring-2 has-[:checked]:ring-indigo-500/20">
                        <input
                          type="radio"
                          name="addr"
                          className="mt-1"
                          checked={
                            String(selectedAddressId ?? '') === String(a.address_id ?? '')
                          }
                          onChange={() => {
                            setSelectedAddressId(a.address_id);
                            setCurrentStep(3);
                          }}
                        />
                        <span className="min-w-0 text-sm">
                          <span className="font-medium text-gray-900">{a.line1}</span>
                          {a.line2 ? <span className="text-gray-600">, {a.line2}</span> : null}
                          <br />
                          <span className="text-gray-600">
                            {a.city} · {a.pincode}
                          </span>
                          {a.label ? (
                            <span className="ml-2 text-xs text-gray-400">({a.label})</span>
                          ) : null}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}

              <h3 className="mb-3 text-sm font-semibold text-gray-800">Add address</h3>

              {mapsApiKey ? (
                <div className="mb-4 rounded-xl border border-indigo-200 bg-indigo-50/40 p-4">
                  <label className={labelClass()}>Search address</label>
                  {mapsLoadError ? (
                    <p className="mb-2 text-sm text-red-700">{mapsLoadError}</p>
                  ) : !mapsReady ? (
                    <p className="mb-2 text-sm text-gray-600">Loading…</p>
                  ) : null}
                  {addressPredictionsError ? (
                    <p className="mb-2 text-sm text-red-700">{addressPredictionsError}</p>
                  ) : null}
                  <div className="relative">
                    <input
                      type="text"
                      className={inputClass()}
                      placeholder="e.g. 123 Main St, Toronto"
                      autoComplete="off"
                      value={addressSearchText}
                      onChange={(e) => {
                        setAddressPredictionsPaused(false);
                        setAddressSearchText(e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (!addressPredictions.length) return;
                        if (e.key === 'ArrowDown') {
                          e.preventDefault();
                          setAddressPickHighlight((i) =>
                            Math.min(i + 1, addressPredictions.length - 1)
                          );
                        } else if (e.key === 'ArrowUp') {
                          e.preventDefault();
                          setAddressPickHighlight((i) => Math.max(i - 1, 0));
                        } else if (e.key === 'Enter') {
                          e.preventDefault();
                          const p = addressPredictions[addressPickHighlight];
                          if (p) selectAddressPrediction(p);
                        } else if (e.key === 'Escape') {
                          clearAddressPredictions();
                          setAddressPredictionsPaused(false);
                        }
                      }}
                      disabled={!mapsReady || addressPickLoading}
                    />
                    {addressPredictLoading && addressSearchText.trim().length >= 3 ? (
                      <p className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">Searching…</p>
                    ) : null}
                    {addressPredictions.length > 0 ? (
                      <ul
                        className="absolute left-0 right-0 z-[200] mt-1 max-h-60 overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
                        role="listbox"
                      >
                        {addressPredictions.map((p, idx) => {
                          const main = p.structured_formatting?.main_text || p.description || 'Address';
                          const sec = p.structured_formatting?.secondary_text;
                          return (
                            <li key={p.place_id}>
                              <button
                                type="button"
                                role="option"
                                aria-selected={idx === addressPickHighlight}
                                className={`flex w-full flex-col px-3 py-2 text-left text-sm hover:bg-indigo-50 ${
                                  idx === addressPickHighlight ? 'bg-indigo-50' : ''
                                }`}
                                onMouseDown={(ev) => ev.preventDefault()}
                                onClick={() => selectAddressPrediction(p)}
                              >
                                <span className="font-medium text-gray-900">{main}</span>
                                {sec ? <span className="text-xs text-gray-500">{sec}</span> : null}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className="mt-2 text-sm font-medium text-indigo-700 underline hover:text-indigo-900"
                    onClick={() => setShowManualAddressFields((v) => !v)}
                  >
                    {showManualAddressFields ? 'Hide manual fields' : 'Edit fields manually'}
                  </button>
                </div>
              ) : null}

              {(showManualAddressFields || !mapsApiKey) && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label className={labelClass()}>Line 1</label>
                    <input
                      className={inputClass()}
                      value={newAddr.line1}
                      onChange={(e) => setNewAddr((a) => ({ ...a, line1: e.target.value }))}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelClass()}>Line 2</label>
                    <input
                      className={inputClass()}
                      value={newAddr.line2}
                      onChange={(e) => setNewAddr((a) => ({ ...a, line2: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className={labelClass()}>City</label>
                    <input
                      className={inputClass()}
                      value={newAddr.city}
                      onChange={(e) => setNewAddr((a) => ({ ...a, city: e.target.value }))}
                      placeholder="e.g. toronto"
                    />
                  </div>
                  <div>
                    <label className={labelClass()}>Postal code</label>
                    <input
                      className={inputClass()}
                      value={newAddr.pincode}
                      onChange={(e) => setNewAddr((a) => ({ ...a, pincode: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className={labelClass()}>Latitude</label>
                    <input
                      type="number"
                      step="any"
                      className={inputClass()}
                      value={newAddr.latitude}
                      onChange={(e) => setNewAddr((a) => ({ ...a, latitude: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className={labelClass()}>Longitude</label>
                    <input
                      type="number"
                      step="any"
                      className={inputClass()}
                      value={newAddr.longitude}
                      onChange={(e) => setNewAddr((a) => ({ ...a, longitude: e.target.value }))}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelClass()}>Area / label</label>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <input
                        className={inputClass()}
                        placeholder="Area"
                        value={newAddr.area}
                        onChange={(e) => setNewAddr((a) => ({ ...a, area: e.target.value }))}
                      />
                      <input
                        className={inputClass()}
                        placeholder="Label (e.g. Home)"
                        value={newAddr.label}
                        onChange={(e) => setNewAddr((a) => ({ ...a, label: e.target.value }))}
                      />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-gray-700 sm:col-span-2">
                    <input
                      type="checkbox"
                      checked={newAddr.skip_serviceability}
                      onChange={(e) =>
                        setNewAddr((a) => ({ ...a, skip_serviceability: e.target.checked }))
                      }
                    />
                    Skip serviceability check (admin override)
                  </label>
                </div>
              )}

              {mapsApiKey && !showManualAddressFields ? (
                <div className="mb-4 rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-700">
                  <p className="font-medium text-gray-900">Filled from map</p>
                  <p className="mt-1 text-xs text-gray-600">
                    <span className="font-medium">Line 1:</span> {newAddr.line1 || '—'}
                  </p>
                  {newAddr.line2 ? (
                    <p className="text-xs text-gray-600">
                      <span className="font-medium">Line 2:</span> {newAddr.line2}
                    </p>
                  ) : null}
                  {newAddr.area ? (
                    <p className="text-xs text-gray-600">
                      <span className="font-medium">Area:</span> {newAddr.area}
                    </p>
                  ) : null}
                  <p className="text-xs text-gray-600">
                    <span className="font-medium">City:</span> {newAddr.city || '—'}
                  </p>
                  <p className="text-xs text-gray-600">
                    <span className="font-medium">Postal:</span> {newAddr.pincode || '—'}
                  </p>
                  <p className="text-xs text-gray-600">
                    <span className="font-medium">Coordinates:</span>{' '}
                    {newAddr.latitude && newAddr.longitude
                      ? `${newAddr.latitude}, ${newAddr.longitude}`
                      : '—'}
                  </p>
                  <div className="mt-3">
                    <label className={labelClass()}>Label (optional)</label>
                    <input
                      className={inputClass()}
                      placeholder="e.g. Home"
                      value={newAddr.label}
                      onChange={(e) => setNewAddr((a) => ({ ...a, label: e.target.value }))}
                    />
                  </div>
                  <label className="mt-3 flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={newAddr.skip_serviceability}
                      onChange={(e) =>
                        setNewAddr((a) => ({ ...a, skip_serviceability: e.target.checked }))
                      }
                    />
                    Skip serviceability check (admin override)
                  </label>
                </div>
              ) : null}
              <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={handleCreateAddress}
                  disabled={addrSubmitting}
                  className="rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {addrSubmitting ? 'Saving…' : 'Save address'}
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentStep(3)}
                  disabled={!selectedAddressId}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                >
                  Continue to order
                </button>
              </div>
            </Panel>
          )}

          {currentStep === 3 && (
            <Panel
              title="Order details"
              subtitle={
                catalogLoading
                  ? 'Loading services…'
                  : 'Primary time = expected start; add more slots to send time_options for inspection.'
              }
            >
              {selectedUser && (
                <p className="mb-2 text-sm text-gray-600">
                  {selectedUser.name} · {selectedUser.mobile} · address ID{' '}
                  <code className="rounded bg-gray-100 px-1">{selectedAddressId}</code>
                </p>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="relative sm:col-span-2">
                  <label className={labelClass()}>Service</label>
                  <input
                    type="text"
                    className={inputClass()}
                    placeholder="Type to search by name or ID, then pick a row"
                    value={serviceSearch}
                    disabled={catalogLoading}
                    autoComplete="off"
                    onChange={(e) => {
                      setServiceSearch(e.target.value);
                      setServiceMenuOpen(true);
                      setOrderForm((f) => ({ ...f, service_id: '' }));
                    }}
                    onFocus={() => !catalogLoading && setServiceMenuOpen(true)}
                    onBlur={() => {
                      setTimeout(() => setServiceMenuOpen(false), 200);
                    }}
                    onKeyDown={(e) => {
                      if (!serviceMenuOpen || !filteredServices.length) {
                        if (e.key === 'Escape') setServiceMenuOpen(false);
                        return;
                      }
                      if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        setServiceHighlightIdx((i) =>
                          Math.min(i + 1, filteredServices.length - 1)
                        );
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        setServiceHighlightIdx((i) => Math.max(i - 1, 0));
                      } else if (e.key === 'Enter') {
                        e.preventDefault();
                        const s = filteredServices[serviceHighlightIdx];
                        if (s) pickService(s);
                      } else if (e.key === 'Escape') {
                        setServiceMenuOpen(false);
                      }
                    }}
                  />
                  {serviceMenuOpen && !catalogLoading ? (
                    <ul
                      className="absolute left-0 right-0 z-[100] mt-1 max-h-64 overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
                      role="listbox"
                    >
                      {filteredServices.length === 0 ? (
                        <li className="px-3 py-2 text-sm text-gray-500">No matching services</li>
                      ) : (
                        filteredServices.map((s, idx) => (
                          <li key={s.service_id}>
                            <button
                              type="button"
                              role="option"
                              aria-selected={idx === serviceHighlightIdx}
                              className={`flex w-full px-3 py-2 text-left text-sm hover:bg-indigo-50 ${
                                idx === serviceHighlightIdx ? 'bg-indigo-50' : ''
                              }`}
                              onMouseDown={(ev) => ev.preventDefault()}
                              onClick={() => pickService(s)}
                            >
                              <span className="font-medium text-gray-900">{s.title}</span>
                              <span className="ml-2 text-gray-600">
                                {formatCentsAsCurrency(s.amount, s.currency || 'CAD')}
                                {s.service_type ? (
                                  <span className="text-gray-400"> · {s.service_type}</span>
                                ) : null}
                              </span>
                              <span className="ml-auto shrink-0 font-mono text-xs text-gray-400">
                                {s.service_id}
                              </span>
                            </button>
                          </li>
                        ))
                      )}
                    </ul>
                  ) : null}
                </div>

                <div className="sm:col-span-2 space-y-3">
                  <div className="flex flex-wrap items-end justify-between gap-2">
                    <div>
                      <label className={labelClass()}>Preferred start times</label>
                      <p className="text-xs text-gray-500">
                        Primary (earliest): used as expected start · additional slots populate customer time options when supported by the backend.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setPreferredSlots((prev) => {
                          const last = prev[prev.length - 1];
                          const base =
                            last instanceof Date && !Number.isNaN(last.getTime())
                              ? last.getTime()
                              : Date.now();
                          return [...prev, new Date(base + 3600000)];
                        })
                      }
                      className="shrink-0 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-800 hover:bg-indigo-100"
                    >
                      + Add slot
                    </button>
                  </div>
                  <ul className="space-y-3">
                    {preferredSlots.map((slot, idx) => (
                      <li
                        key={idx}
                        className="flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-3"
                      >
                        <div className="min-w-0 flex-1">
                          <label className={labelClass()}>
                            {idx === 0 ? 'Slot 1 (primary)' : `Slot ${idx + 1}`}
                          </label>
                          <DatePicker
                            selected={slot}
                            onChange={(d) => {
                              if (!d) return;
                              setPreferredSlots((prev) => {
                                const next = [...prev];
                                next[idx] = d;
                                return next;
                              });
                            }}
                            showTimeSelect
                            dateFormat="Pp"
                            className={inputClass()}
                            wrapperClassName="w-full block"
                          />
                        </div>
                        {preferredSlots.length > 1 ? (
                          <button
                            type="button"
                            onClick={() =>
                              setPreferredSlots((prev) => prev.filter((_, i) => i !== idx))
                            }
                            className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-800 hover:bg-red-100 sm:mb-0.5"
                          >
                            Remove
                          </button>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>

                {selectedService?.service_type === 'QUANTITY' && (
                  <div>
                    <label className={labelClass()}>Quantity</label>
                    <input
                      type="number"
                      min={1}
                      className={inputClass()}
                      value={orderForm.quantity}
                      onChange={(e) =>
                        setOrderForm((f) => ({ ...f, quantity: e.target.value }))
                      }
                    />
                  </div>
                )}

                <div className="sm:col-span-2">
                  <label className={labelClass()}>Preferences (comma or newline)</label>
                  <textarea
                    className={inputClass()}
                    rows={2}
                    value={orderForm.preferences}
                    onChange={(e) =>
                      setOrderForm((f) => ({ ...f, preferences: e.target.value }))
                    }
                  />
                </div>

                <label className="flex items-center gap-2 text-sm text-gray-700 sm:col-span-2">
                  <input
                    type="checkbox"
                    checked={orderForm.is_insured}
                    onChange={(e) =>
                      setOrderForm((f) => ({ ...f, is_insured: e.target.checked }))
                    }
                  />
                  Insured
                </label>

                <div className="sm:col-span-2">
                  <label className={labelClass()}>Customer remark</label>
                  <textarea
                    className={inputClass()}
                    rows={2}
                    value={orderForm.user_remark}
                    onChange={(e) =>
                      setOrderForm((f) => ({ ...f, user_remark: e.target.value }))
                    }
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className={labelClass()}>Customer photos (optional)</label>
                  <p className="mb-2 text-xs text-gray-500">Upload images from this device — same upload as other admin tools.</p>
                  <input
                    ref={orderImagesInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={async (e) => {
                      const files = Array.from(e.target.files || []);
                      e.target.value = '';
                      if (files.length === 0) return;
                      setOrderUserImagesUploading(true);
                      try {
                        const uploadPromises = files.map(async (file) => {
                          const type = String(file?.type || '').toLowerCase();
                          const name = String(file?.name || '').toLowerCase();
                          const looksLikeImage =
                            type.startsWith('image/') ||
                            (!type && /\.(jpe?g|png|webp|gif|heic|heif|bmp|tiff?)$/i.test(name));
                          if (!looksLikeImage) {
                            toast.error(`Skipped (not an image): ${file.name}`);
                            return null;
                          }
                          try {
                            const url = await uploadAdminImageFile(file);
                            return { status: 'fulfilled', value: url, name: file.name };
                          } catch (err) {
                            return { status: 'rejected', reason: err, name: file.name };
                          }
                        });

                        const results = await Promise.all(uploadPromises);
                        const urls = [];
                        let successCount = 0;

                        results.forEach((res) => {
                          if (!res) return;
                          if (res.status === 'fulfilled') {
                            urls.push(res.value);
                            successCount++;
                          } else {
                            toast.error(`Failed to upload ${res.name}: ${res.reason?.message || 'Upload failed'}`);
                          }
                        });

                        if (urls.length > 0) {
                          setOrderUserImages((prev) => [...prev, ...urls]);
                        }
                        if (successCount > 0) {
                          toast.success(
                            successCount === 1 ? 'Photo uploaded' : `${successCount} photos uploaded`
                          );
                        }
                      } catch (err) {
                        toast.error(err?.message || 'Upload process failed');
                      } finally {
                        setOrderUserImagesUploading(false);
                      }
                    }}
                  />
                  <div className="flex flex-wrap items-end gap-3">
                    {orderUserImages.map((url, i) => (
                      <div
                        key={`${url}-${i}`}
                        className="relative h-20 w-20 overflow-hidden rounded-lg border border-gray-200 bg-gray-100"
                      >
                        <img src={url} alt="" className="h-full w-full object-cover" />
                        <button
                          type="button"
                          onClick={() => setOrderUserImages((prev) => prev.filter((_, j) => j !== i))}
                          className="absolute right-0 top-0 rounded-bl bg-black/60 px-1.5 py-0.5 text-xs font-bold text-white hover:bg-black/80"
                          aria-label="Remove photo"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      disabled={orderUserImagesUploading}
                      onClick={() => orderImagesInputRef.current?.click()}
                      className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 shadow-sm hover:bg-gray-50 disabled:opacity-50"
                    >
                      {orderUserImagesUploading ? 'Uploading…' : 'Upload photo'}
                    </button>
                  </div>
                </div>

                {selectedService?.service_type === 'AUTO' && (
                  <>
                    <div>
                      <label className={labelClass()}>Build year</label>
                      <input
                        type="number"
                        min={1900}
                        max={2100}
                        className={inputClass()}
                        value={orderForm.build_year}
                        onChange={(e) =>
                          setOrderForm((f) => ({ ...f, build_year: e.target.value }))
                        }
                      />
                    </div>
                    <div>
                      <label className={labelClass()}>Model</label>
                      <input
                        className={inputClass()}
                        value={orderForm.model}
                        onChange={(e) =>
                          setOrderForm((f) => ({ ...f, model: e.target.value }))
                        }
                      />
                    </div>
                    <div>
                      <label className={labelClass()}>Company</label>
                      <input
                        className={inputClass()}
                        value={orderForm.company}
                        onChange={(e) =>
                          setOrderForm((f) => ({ ...f, company: e.target.value }))
                        }
                      />
                    </div>
                    <div>
                      <label className={labelClass()}>Colour</label>
                      <input
                        className={inputClass()}
                        value={orderForm.colour}
                        onChange={(e) =>
                          setOrderForm((f) => ({ ...f, colour: e.target.value }))
                        }
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelClass()}>VIN</label>
                      <input
                        className={inputClass()}
                        value={orderForm.vin_no}
                        onChange={(e) =>
                          setOrderForm((f) => ({ ...f, vin_no: e.target.value }))
                        }
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
                <button
                  type="button"
                  onClick={() => setCurrentStep(2)}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  ← Back to address
                </button>
                <button
                  type="button"
                  onClick={handleCreateOrder}
                  disabled={createSubmitting || catalogLoading || orderUserImagesUploading}
                  className="rounded-lg bg-green-600 px-6 py-3 text-sm font-bold text-white shadow hover:bg-green-700 disabled:opacity-50"
                >
                  {createSubmitting ? 'Creating…' : 'Create order'}
                </button>
              </div>
            </Panel>
          )}
        </div>
      </main>
    </div>
  );
}

export default AdminOrderCreate;
